import { useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import { ControlPointOutlined } from "@mui/icons-material";

import { calculateTotalBill } from "./ApptBills/helpers";
import { inject } from "../../../../../../../lib/itn-ui-kit/services/service-container";
import ApptBillsActions from "./ApptBills/ApptBillsActions";
import ApptBillsList from "./ApptBills/ApptBillsList";
import CreateApptProcedurev2 from "./CreateApptProcedurev2";
import DeleteApptProcedurev2 from "./DeleteApptProcedurev2";
import Modal from "../../../../../global/Modal";
import useWindowDimensions from "../../../../../../hooks/useWindowDimensions";

export default function ProviderApptProceduresv2({
  caseInfo,
  selectedAppointment,
  proCliTrigger,
  setProCliTrigger
}) {
  const currentUserService = inject("current-user-service");
  const { width } = useWindowDimensions();
  const [openAddBill, setOpenAddBill] = useState(false);
  const [openDeleteBills, setOpenDeleteBills] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);

  const administeredServices =
    selectedAppointment?.administered_services ||
    selectedAppointment?.administeredServices;

  const totalBill = calculateTotalBill(administeredServices);
  const canView = currentUserService?.userRoles?.permissions.includes(
    "provider.view_administeredservices",
  );
  const canAdd =
    currentUserService?.userRoles?.permissions.includes(
      "provider.add_administeredservices",
    ) && caseInfo;
  const canCreate = useMemo(
    () =>
      selectedAppointment &&
      canAdd,
    [selectedAppointment, canAdd],
  );

  function handleOpenAddModal(editionMode) {
    setIsEditing(editionMode);
    setTimeout(() => {
      setOpenAddBill(true);
    }, 500);
  }

  if (!canView) {
    return null;
  }

  return (
    <div className="relative">
      {/* Notes for small screens */}
      {width < 640 && selectedAppointment?.notes && (
        <div className="w-full mb-4 border rounded-md border-gray">
          <h3 className="px-6 py-2 underline">Appointment Notes</h3>
          <div
            className="p-4 notes"
            dangerouslySetInnerHTML={{ __html: selectedAppointment.notes }}
          />
        </div>
      )}

      <div className="grid grid-cols-[1fr_3fr_1fr] mb-4 gap-4">
        {/* Empty space */}
        <div></div>

        {/* Bill Total */}
        <span className="flex flex-wrap w-full text-center bg-red-300 rounded-md place-content-center justify-self-end">
          <b className="mr-2">Total Service Cost:</b>
          {totalBill}
        </span>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <ApptBillsActions
            selectedBills={selectedBills}
            handleOpenAddModal={() => handleOpenAddModal(true)}
            handleOpenDeleteModal={() => setOpenDeleteBills(true)}
          />

          {canCreate && (
            <Tooltip
              className="w-fit"
              title="Add Administered Service"
              placement="top"
            >
              <a
                onClick={() => handleOpenAddModal(false)}
                className="flex items-center px-2 text-green-500 rounded-lg cursor-pointer hover:text-white hover:bg-green-500"
              >
                <ControlPointOutlined />
              </a>
            </Tooltip>
          )}
        </div>
      </div>

      {/* List */}
      <ApptBillsList
        setSelectedBills={setSelectedBills}
        bills={administeredServices || []}
      />

      {/* Modals */}
      {openAddBill && (
        <Modal
          title={`${isEditing ? "Update" : "Create"} Administered Services`}
          open={openAddBill}
          maxWidth="md"
          onClose={() => setOpenAddBill(false)}
        >
          <CreateApptProcedurev2
            selectedAppt={selectedAppointment}
            setOpenAddBill={setOpenAddBill}
            caseInfo={caseInfo}
            selectedBills={isEditing ? selectedBills : []}
            setProCliTrigger={setProCliTrigger}
            proCliTrigger={proCliTrigger}
          />
        </Modal>
      )}
      {openDeleteBills && (
        <Modal
          maxWidth="xl"
          onClose={() => setOpenDeleteBills(false)}
          open
          title={
            <h2 className="text-2xl text-center text-red-500 capitalize">
              Delete Administered Service<span className="lowercase">(s)</span>
            </h2>
          }
        >
          <DeleteApptProcedurev2
            selectedAppt={selectedAppointment}
            selectedBills={selectedBills}
            setOpenDeleteBills={setOpenDeleteBills}
            setProCliTrigger={setProCliTrigger}
            proCliTrigger={proCliTrigger}
          />
        </Modal>
      )}
    </div>
  );
}
